/**
 * 表单模型配置统一文件
 *
 * 此文件集中管理所有表单类型、模型版本相关的配置，包括：
 * - 每个表单类型支持的模型版本
 * - 每个模型版本支持哪些设置选项及其可选值
 * - 每个表单类型、每个模型版本显示哪些设置项UI组件
 * - 哪些选项需要会员权限
 */
import type { FormSettingsKey, FormTabType, ModelVersion, SubsPlanType, TaskSettings, UserTagKey } from '@/app/types';
import { isEnterpriseDistribution } from './distribution';
import { SITE } from './site';

type ModelCapabilitiesOptionType = Partial<Record<keyof TaskSettings, readonly string[]>>;

/**
 * 模型版本常量映射
 * 用于统一管理模型版本字符串，提高代码可读性和可维护性
 */
export const MODEL_VERSION = {
  /** Q1 模型版本 */
  Q1: '3.0',
  /** Q2 模型版本 */
  Q2: '3.1',
  /** Q2 Pro 模型版本 */
  Q2_PRO: '3.1_pro',
  /** Q3 模型版本 */
  Q3: '3.2',
  /** Q3 Pro 模型版本 */
  Q3_PRO: '3.2_pro_m',
  /** Q3 Fast 模型版本 */
  Q3_FAST: '3.2_fast_m',
  /** Q3 Lite 模型版本 */
  Q3_LITE: '3.2_lite_m',
  /** GPT Image 2 模型版本 */
  GPT_IMAGE_2: '3.2_image_2',
  /** GPT Image 2.5 Fast 模型版本 */
  GPT_IMAGE_2_5_FAST: '3.2_image_2_5_fast',
  /** GPT Image 2.5 Pro 模型版本 */
  GPT_IMAGE_2_5_PRO: '3.2_image_2_5_pro',
  /** Q3 A 模型版本 */
  Q3_A: '3.2_a',
  /** Happyhorse 1.0 模型版本 */
  Q3_HH: '3.2_hh',
  /** 2.0 模型版本（旧版本） */
  '2.0': '2.0',
} as const;

/** 参考生 Vidu Q3「营销广告」生成模式（对应接口 transition） */
export const TASK_TRANSITION_PRO_AD = 'pro_ad' as const;

export const LATEST_MODEL_VERSION = MODEL_VERSION.Q2; // 当前最新的模型版本

export const MODEL_VERSION_SETTINGS_MAP: Record<ModelVersion, FormSettingsKey> = {
  [MODEL_VERSION['2.0']]: 'settingsV2_0',
  [MODEL_VERSION.Q1]: 'settingsQ1',
  [MODEL_VERSION.Q2]: 'settingsQ2',
  [MODEL_VERSION.Q2_PRO]: 'settingsQ2_pro',
  [MODEL_VERSION.Q3]: 'settingsQ3',
  [MODEL_VERSION.Q3_PRO]: 'settingsQ3_pro',
  [MODEL_VERSION.Q3_FAST]: 'settingsQ3_fast',
  [MODEL_VERSION.Q3_LITE]: 'settingsQ3_lite',
  [MODEL_VERSION.GPT_IMAGE_2]: 'settingsGPTImage2',
  [MODEL_VERSION.GPT_IMAGE_2_5_FAST]: 'settingsGPTImage25Fast',
  [MODEL_VERSION.GPT_IMAGE_2_5_PRO]: 'settingsGPTImage25Pro',
  [MODEL_VERSION.Q3_A]: 'settingsQ3_a',
  [MODEL_VERSION.Q3_HH]: 'settingsQ3_hh',
};

export enum TASK_SETTING_OPTION {
  ModelVersion = 'ModelVersion',
  Style = 'Style',
  MovementAmplitude = 'MovementAmplitude',
  Amount = 'Amount',
  NonPeakMode = 'NonPeakMode',
  StylePreference = 'StylePreference',
  /** 时长 & 清晰度 */
  DurationAndResolution = 'DurationAndResolution',
  /** 清晰度 */
  Resolution = 'Resolution',
  /** 运动幅度 & 宽高比 */
  MovementAmplitudeAndAspectRatio = 'MovementAmplitudeAndResolution',
  /** 宽高比 */
  AspectRatio = 'AspectRatio',
  /** 生成模式 */
  CreationMode = 'CreationMode',
  /** 时长 */
  Duration = 'Duration',
  /** 音效 & 语音 */
  AudioAndLipSync = 'AudioAndLipSync',
  /** 视频延长模型版本 */
  ExtendModalVersion = 'ExtendModalVersion',
  /** 宫格数 */
  GridCount = 'GridCount',
  /** 清晰度 & 编码格式 */
  ResolutionAndCodec = 'ResolutionAndCodec',
  /** 编码格式 */
  Codec = 'Codec',
  /** 场景 */
  Scenario = 'Scenario',
  /** 配音语言 */
  VoiceLanguage = 'VoiceLanguage',
  /** 字幕 */
  Subtitle = 'Subtitle',
  /** 保留原始音频 */
  RetainOriginalAudio = 'RetainOriginalAudio',
}

export const DEFAULT_SAMPLE_COUNT = 1;

/**
 * 每个表单类型支持的模型版本配置
 * 定义哪些表单类型可以使用哪些模型版本
 */
export const AVAILABLE_MODEL_VERSIONS: Record<Exclude<FormTabType, 'scene'>, readonly ModelVersion[]> = {
  character2video: [
    MODEL_VERSION['2.0'],
    MODEL_VERSION.Q1,
    MODEL_VERSION.Q2,
    MODEL_VERSION.Q2_PRO,
    MODEL_VERSION.Q3,
    ...(isEnterpriseDistribution ? [MODEL_VERSION.Q3_A, MODEL_VERSION.Q3_HH] : []),
  ],
  img2video: [
    MODEL_VERSION['2.0'],
    MODEL_VERSION.Q1,
    MODEL_VERSION.Q2,
    MODEL_VERSION.Q3,
    ...(isEnterpriseDistribution ? [MODEL_VERSION.Q3_A, MODEL_VERSION.Q3_HH] : []),
  ],
  text2video: [
    MODEL_VERSION.Q1,
    MODEL_VERSION.Q2,
    MODEL_VERSION.Q3,
    ...(isEnterpriseDistribution ? [MODEL_VERSION.Q3_A, MODEL_VERSION.Q3_HH] : []),
  ],
  reference2image: [
    MODEL_VERSION.Q1,
    MODEL_VERSION.Q2,
    MODEL_VERSION.Q3_PRO,
    MODEL_VERSION.Q3_FAST,
    MODEL_VERSION.Q3_LITE,
    MODEL_VERSION.GPT_IMAGE_2,
    MODEL_VERSION.GPT_IMAGE_2_5_FAST,
    MODEL_VERSION.GPT_IMAGE_2_5_PRO,
  ],
  text2image: [
    MODEL_VERSION.Q2,
    MODEL_VERSION.Q3_PRO,
    MODEL_VERSION.Q3_FAST,
    MODEL_VERSION.Q3_LITE,
    MODEL_VERSION.GPT_IMAGE_2,
    MODEL_VERSION.GPT_IMAGE_2_5_FAST,
    MODEL_VERSION.GPT_IMAGE_2_5_PRO,
  ],
};

/**
 * 内测模型版本访问配置
 * 声明哪些 (表单类型 + 模型版本) 组合处于内测阶段，以及所需的用户标签。
 * 内测结束、全量开放后，直接删除对应条目即可，无需改动其他逻辑。
 *
 * 未来新增内测示例：
 *   img2video: { [MODEL_VERSION.Q4]: 'isQ4I2V' }
 */
export const BETA_MODEL_VERSIONS: Partial<
  Record<Exclude<FormTabType, 'scene'>, Partial<Record<ModelVersion, UserTagKey>>>
> = {};

/**
 * 按模型版本与表单类型声明支持哪些设置键及其可选值
 * options 定义各类型通用的设置选项，perTypeOverrides 定义针对特定类型的特殊设置选项
 */
export const MODEL_CAPABILITIES: Record<
  FormSettingsKey,
  {
    options: ModelCapabilitiesOptionType;
    perTypeOverrides: Partial<Record<FormTabType, ModelCapabilitiesOptionType>>;
  }
> = {
  settingsV2_0: {
    options: {
      duration: ['4', '8'],
      resolution: ['512', '720p', '1080p'],
      aspect_ratio: ['16:9', '9:16', '1:1'],
      codec: ['h265', 'h265_pro', 'h264', 'h264_pro'],
      // movement_amplitude: ['auto', 'small', 'medium', 'large'],
      sample_count: ['1', '2', '3', '4'],
      schedule_mode: ['normal', 'nopeak'],
    },
    perTypeOverrides: {
      character2video: {
        resolution: ['512', '720p'], // 2.0 模型参考生视频不支持 1080p
        duration: ['4'], // 2.0 模型参考生视频不支持 8s
      },
    },
  },
  settingsQ1: {
    options: {
      duration: ['5'],
      resolution: ['1080p'],
      aspect_ratio: ['16:9', '9:16', '1:1'],
      codec: ['h265', 'h265_pro', 'h264', 'h264_pro'],
      transition: [],
      style: [],
      // movement_amplitude: ['auto', 'small', 'medium', 'large'],
      sample_count: ['1', '2', '3', '4'],
      schedule_mode: ['normal', 'nopeak'],
    },
    perTypeOverrides: {
      img2video: {
        transition: ['creative', 'stable'],
      },
      text2video: {
        style: ['general', 'anime'],
      },
    },
  },
  settingsQ2: {
    options: {
      duration: ['2', '3', '4', '5', '6', '7', '8'],
      resolution: ['1080p'],
      aspect_ratio: ['16:9', '9:16', '1:1', '4:3', '3:4'],
      codec: ['h265', 'h265_pro', 'h264', 'h264_pro'],
      transition: [],
      // dubbing mode (参考生 only)
      sample_count: ['1', '2', '3', '4'],
      schedule_mode: ['normal', 'nopeak'],
    },
    perTypeOverrides: {
      img2video: {
        transition: ['speed', 'pro'],
      },
      reference2image: {
        aspect_ratio: ['intelligent', '16:9', '9:16', '1:1', '4:3', '3:4', 'custom'],
        resolution: ['1080p', '2k', '4k'],
      },
      text2image: {
        aspect_ratio: ['intelligent', '16:9', '9:16', '1:1', '4:3', '3:4', 'custom'],
        resolution: ['1080p', '2k', '4k'],
      },
    },
  },
  settingsQ2_pro: {
    options: {
      duration: ['auto', '2', '3', '4', '5', '6', '7', '8'],
      resolution: ['1080p'],
      aspect_ratio: ['intelligent', '16:9', '9:16', '1:1', '4:3', '3:4'],
      codec: ['h265', 'h265_pro', 'h264', 'h264_pro'],
      // dubbing mode
      sample_count: ['1', '2', '3', '4'],
      schedule_mode: ['normal', 'nopeak'],
    },
    perTypeOverrides: {},
  },
  settingsQ3: {
    options: {
      duration: ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12', '13', '14', '15', '16'],
      resolution: ['1080p'],
      aspect_ratio: ['16:9', '9:16', '1:1', '4:3', '3:4'],
      codec: ['h265', 'h265_pro', 'h264', 'h264_pro'],
      transition: ['pro', 'speed'],
      sample_count: ['1', '2', '3', '4'],
      schedule_mode: ['normal'], // Q3 模型不支持错峰模式
    },
    perTypeOverrides: {
      character2video: {
        transition: ['speed', 'pro', 'pro_ad'],
      },
    },
  },
  settingsQ3_pro: {
    options: {
      resolution: ['1080p', '2k', '4k'],
      aspect_ratio: ['16:9', '9:16', '1:1', '4:3', '3:4'],
      sample_count: ['1', '2', '3', '4'],
    },
    perTypeOverrides: {},
  },
  settingsQ3_fast: {
    options: {
      resolution: ['1080p', '2k', '4k'],
      aspect_ratio: ['16:9', '9:16', '1:1', '4:3', '3:4'],
      sample_count: ['1', '2', '3', '4'],
    },
    perTypeOverrides: {},
  },
  settingsQ3_lite: {
    options: {
      resolution: ['1080p'],
      aspect_ratio: ['16:9', '9:16', '1:1', '4:3', '3:4'],
      sample_count: ['1', '2', '3', '4'],
    },
    perTypeOverrides: {},
  },
  settingsGPTImage2: {
    options: {
      resolution: ['1080p', '2k', '4k'],
      aspect_ratio: ['16:9', '9:16', '1:1', '4:3', '3:4'],
      sample_count: ['1', '2', '3', '4'],
    },
    perTypeOverrides: {},
  },
  settingsGPTImage25Fast: {
    options: {
      resolution: ['1080p', '2k', '4k'],
      aspect_ratio: ['16:9', '9:16', '1:1', '4:3', '3:4'],
      sample_count: ['1', '2', '3', '4'],
    },
    perTypeOverrides: {},
  },
  settingsGPTImage25Pro: {
    options: {
      resolution: ['1080p', '2k', '4k'],
      aspect_ratio: ['16:9', '9:16', '1:1', '4:3', '3:4'],
      sample_count: ['1', '2', '3', '4'],
    },
    perTypeOverrides: {},
  },
  settingsQ3_a: {
    options: {
      duration: ['4', '5', '6', '7', '8', '9', '10', '11', '12', '13', '14', '15'],
      resolution: ['720p', '1080p'],
      aspect_ratio: ['21:9', '9:21', '16:9', '9:16', '1:1', '4:3', '3:4'],
      sample_count: ['1', '2', '3', '4'],
      schedule_mode: ['normal'],
    },
    perTypeOverrides: {},
  },
  settingsQ3_hh: {
    options: {
      duration: ['4', '5', '6', '7', '8', '9', '10', '11', '12', '13', '14', '15'],
      resolution: ['720p', '1080p'],
      aspect_ratio: ['21:9', '9:21', '16:9', '9:16', '1:1', '4:3', '3:4'],
      sample_count: ['1', '2', '3', '4'],
      schedule_mode: ['normal'],
    },
    perTypeOverrides: {},
  },
};

/**
 * 表单类型、表单数据对应显示的设置项配置
 * 定义每个表单类型、每个模型版本显示哪些设置项UI组件
 */
export const SETTINGS_CONFIG: Record<Exclude<FormTabType, 'scene'>, Record<FormSettingsKey, TASK_SETTING_OPTION[]>> = {
  character2video: {
    settingsV2_0: [
      TASK_SETTING_OPTION.ModelVersion,
      TASK_SETTING_OPTION.DurationAndResolution,
      TASK_SETTING_OPTION.MovementAmplitudeAndAspectRatio,
      TASK_SETTING_OPTION.Amount,
      TASK_SETTING_OPTION.Codec,
      TASK_SETTING_OPTION.NonPeakMode,
    ],
    settingsQ1: [
      TASK_SETTING_OPTION.ModelVersion,
      TASK_SETTING_OPTION.DurationAndResolution,
      TASK_SETTING_OPTION.MovementAmplitudeAndAspectRatio,
      TASK_SETTING_OPTION.Amount,
      TASK_SETTING_OPTION.Codec,
      TASK_SETTING_OPTION.NonPeakMode,
    ],
    settingsQ2: [
      TASK_SETTING_OPTION.ModelVersion,
      TASK_SETTING_OPTION.Duration,
      TASK_SETTING_OPTION.AudioAndLipSync,
      TASK_SETTING_OPTION.ResolutionAndCodec,
      TASK_SETTING_OPTION.AspectRatio,
      TASK_SETTING_OPTION.Amount,
      TASK_SETTING_OPTION.NonPeakMode,
    ],
    settingsQ2_pro: [
      TASK_SETTING_OPTION.ModelVersion,
      TASK_SETTING_OPTION.Duration,
      TASK_SETTING_OPTION.AudioAndLipSync,
      TASK_SETTING_OPTION.ResolutionAndCodec,
      TASK_SETTING_OPTION.AspectRatio,
      TASK_SETTING_OPTION.Amount,
      TASK_SETTING_OPTION.NonPeakMode,
    ],
    settingsQ3: [
      TASK_SETTING_OPTION.ModelVersion,
      TASK_SETTING_OPTION.Duration,
      TASK_SETTING_OPTION.ResolutionAndCodec,
      TASK_SETTING_OPTION.CreationMode,
      TASK_SETTING_OPTION.AspectRatio,
      TASK_SETTING_OPTION.Amount,
    ],
    settingsQ3_pro: [],
    settingsQ3_fast: [],
    settingsQ3_lite: [],
    settingsGPTImage2: [],
    settingsGPTImage25Fast: [],
    settingsGPTImage25Pro: [],
    settingsQ3_a: [
      TASK_SETTING_OPTION.ModelVersion,
      TASK_SETTING_OPTION.Duration,
      TASK_SETTING_OPTION.Resolution,
      TASK_SETTING_OPTION.AspectRatio,
      TASK_SETTING_OPTION.Amount,
    ],
    settingsQ3_hh: [
      TASK_SETTING_OPTION.ModelVersion,
      TASK_SETTING_OPTION.Duration,
      TASK_SETTING_OPTION.Resolution,
      TASK_SETTING_OPTION.AspectRatio,
      TASK_SETTING_OPTION.Amount,
    ],
  },
  img2video: {
    settingsV2_0: [
      TASK_SETTING_OPTION.ModelVersion,
      TASK_SETTING_OPTION.ResolutionAndCodec,
      TASK_SETTING_OPTION.MovementAmplitude,
      TASK_SETTING_OPTION.Amount,
      TASK_SETTING_OPTION.NonPeakMode,
    ],
    settingsQ1: [
      TASK_SETTING_OPTION.ModelVersion,
      TASK_SETTING_OPTION.ResolutionAndCodec,
      TASK_SETTING_OPTION.MovementAmplitude,
      TASK_SETTING_OPTION.StylePreference,
      TASK_SETTING_OPTION.Amount,
      TASK_SETTING_OPTION.NonPeakMode,
    ],
    settingsQ2: [
      TASK_SETTING_OPTION.ModelVersion,
      TASK_SETTING_OPTION.ResolutionAndCodec,
      TASK_SETTING_OPTION.CreationMode,
      TASK_SETTING_OPTION.Amount,
      TASK_SETTING_OPTION.NonPeakMode,
    ],
    settingsQ2_pro: [],
    settingsQ3: [
      TASK_SETTING_OPTION.ModelVersion,
      TASK_SETTING_OPTION.ResolutionAndCodec,
      TASK_SETTING_OPTION.CreationMode,
      TASK_SETTING_OPTION.Amount,
    ],
    settingsQ3_pro: [],
    settingsQ3_fast: [],
    settingsQ3_lite: [],
    settingsGPTImage2: [],
    settingsGPTImage25Fast: [],
    settingsGPTImage25Pro: [],
    settingsQ3_a: [
      TASK_SETTING_OPTION.ModelVersion,
      TASK_SETTING_OPTION.Duration,
      TASK_SETTING_OPTION.Resolution,
      TASK_SETTING_OPTION.AspectRatio,
      TASK_SETTING_OPTION.Amount,
    ],
    settingsQ3_hh: [
      TASK_SETTING_OPTION.ModelVersion,
      TASK_SETTING_OPTION.Duration,
      TASK_SETTING_OPTION.Resolution,
      TASK_SETTING_OPTION.AspectRatio,
      TASK_SETTING_OPTION.Amount,
    ],
  },
  text2video: {
    settingsV2_0: [],
    settingsQ1: [
      TASK_SETTING_OPTION.ModelVersion,
      TASK_SETTING_OPTION.Style,
      TASK_SETTING_OPTION.DurationAndResolution,
      TASK_SETTING_OPTION.MovementAmplitudeAndAspectRatio,
      TASK_SETTING_OPTION.Amount,
      TASK_SETTING_OPTION.Codec,
      TASK_SETTING_OPTION.NonPeakMode,
    ],
    settingsQ2: [
      TASK_SETTING_OPTION.ModelVersion,
      TASK_SETTING_OPTION.Duration,
      TASK_SETTING_OPTION.ResolutionAndCodec,
      TASK_SETTING_OPTION.AspectRatio,
      TASK_SETTING_OPTION.Amount,
      TASK_SETTING_OPTION.NonPeakMode,
    ],
    settingsQ2_pro: [],
    settingsQ3: [
      TASK_SETTING_OPTION.ModelVersion,
      TASK_SETTING_OPTION.Duration,
      TASK_SETTING_OPTION.ResolutionAndCodec,
      TASK_SETTING_OPTION.AspectRatio,
      TASK_SETTING_OPTION.CreationMode,
      TASK_SETTING_OPTION.Amount,
    ],
    settingsQ3_pro: [],
    settingsQ3_fast: [],
    settingsQ3_lite: [],
    settingsGPTImage2: [],
    settingsGPTImage25Fast: [],
    settingsGPTImage25Pro: [],
    settingsQ3_a: [
      TASK_SETTING_OPTION.ModelVersion,
      TASK_SETTING_OPTION.Duration,
      TASK_SETTING_OPTION.Resolution,
      TASK_SETTING_OPTION.AspectRatio,
      TASK_SETTING_OPTION.Amount,
    ],
    settingsQ3_hh: [
      TASK_SETTING_OPTION.ModelVersion,
      TASK_SETTING_OPTION.Duration,
      TASK_SETTING_OPTION.Resolution,
      TASK_SETTING_OPTION.AspectRatio,
      TASK_SETTING_OPTION.Amount,
    ],
  },
  reference2image: {
    settingsV2_0: [],
    settingsQ1: [
      TASK_SETTING_OPTION.ModelVersion,
      TASK_SETTING_OPTION.Resolution,
      TASK_SETTING_OPTION.AspectRatio,
      TASK_SETTING_OPTION.Amount,
    ],
    settingsQ2: [
      TASK_SETTING_OPTION.ModelVersion,
      TASK_SETTING_OPTION.Resolution,
      TASK_SETTING_OPTION.AspectRatio,
      TASK_SETTING_OPTION.Amount,
    ],
    settingsQ2_pro: [],
    settingsQ3: [],
    settingsQ3_pro: [
      TASK_SETTING_OPTION.ModelVersion,
      TASK_SETTING_OPTION.Resolution,
      TASK_SETTING_OPTION.AspectRatio,
      TASK_SETTING_OPTION.Amount,
    ],
    settingsQ3_fast: [
      TASK_SETTING_OPTION.ModelVersion,
      TASK_SETTING_OPTION.Resolution,
      TASK_SETTING_OPTION.AspectRatio,
      TASK_SETTING_OPTION.Amount,
    ],
    settingsQ3_lite: [
      TASK_SETTING_OPTION.ModelVersion,
      TASK_SETTING_OPTION.Resolution,
      TASK_SETTING_OPTION.AspectRatio,
      TASK_SETTING_OPTION.Amount,
    ],
    settingsGPTImage2: [
      TASK_SETTING_OPTION.ModelVersion,
      TASK_SETTING_OPTION.Resolution,
      TASK_SETTING_OPTION.AspectRatio,
      TASK_SETTING_OPTION.Amount,
    ],
    settingsGPTImage25Fast: [
      TASK_SETTING_OPTION.ModelVersion,
      TASK_SETTING_OPTION.Resolution,
      TASK_SETTING_OPTION.AspectRatio,
      TASK_SETTING_OPTION.Amount,
    ],
    settingsGPTImage25Pro: [
      TASK_SETTING_OPTION.ModelVersion,
      TASK_SETTING_OPTION.Resolution,
      TASK_SETTING_OPTION.AspectRatio,
      TASK_SETTING_OPTION.Amount,
    ],
    settingsQ3_a: [],
    settingsQ3_hh: [],
  },
  text2image: {
    settingsV2_0: [],
    settingsQ1: [],
    settingsQ2: [
      TASK_SETTING_OPTION.ModelVersion,
      TASK_SETTING_OPTION.Resolution,
      TASK_SETTING_OPTION.AspectRatio,
      TASK_SETTING_OPTION.Amount,
    ],
    settingsQ2_pro: [],
    settingsQ3: [],
    settingsQ3_pro: [
      TASK_SETTING_OPTION.ModelVersion,
      TASK_SETTING_OPTION.Resolution,
      TASK_SETTING_OPTION.AspectRatio,
      TASK_SETTING_OPTION.Amount,
    ],
    settingsQ3_fast: [
      TASK_SETTING_OPTION.ModelVersion,
      TASK_SETTING_OPTION.Resolution,
      TASK_SETTING_OPTION.AspectRatio,
      TASK_SETTING_OPTION.Amount,
    ],
    settingsQ3_lite: [
      TASK_SETTING_OPTION.ModelVersion,
      TASK_SETTING_OPTION.Resolution,
      TASK_SETTING_OPTION.AspectRatio,
      TASK_SETTING_OPTION.Amount,
    ],
    settingsGPTImage2: [
      TASK_SETTING_OPTION.ModelVersion,
      TASK_SETTING_OPTION.Resolution,
      TASK_SETTING_OPTION.AspectRatio,
      TASK_SETTING_OPTION.Amount,
    ],
    settingsGPTImage25Fast: [
      TASK_SETTING_OPTION.ModelVersion,
      TASK_SETTING_OPTION.Resolution,
      TASK_SETTING_OPTION.AspectRatio,
      TASK_SETTING_OPTION.Amount,
    ],
    settingsGPTImage25Pro: [
      TASK_SETTING_OPTION.ModelVersion,
      TASK_SETTING_OPTION.Resolution,
      TASK_SETTING_OPTION.AspectRatio,
      TASK_SETTING_OPTION.Amount,
    ],
    settingsQ3_a: [],
    settingsQ3_hh: [],
  },
};

/**
 * 设置选项需要的订阅计划限制
 * 定义哪些设置选项的哪些值需要特定的订阅计划才能使用
 *
 * 结构说明：
 * - options: 基础限制配置，适用于所有模型版本
 * - perVersionOverrides: 针对特定模型版本的覆盖配置
 *   - 如果某个值在 override 中设置为空数组 []，表示该版本下所有用户（包括免费用户）都可以使用
 *   - 如果某个值在 override 中设置为特定计划数组，则覆盖基础配置
 *
 */
export const SETTINGS_PLAN_RESTRICTIONS: Record<
  string,
  {
    options: { [key: string]: SubsPlanType[] };
    perVersionOverrides?: Partial<Record<ModelVersion, { [key: string]: SubsPlanType[] }>>;
  }
> = {
  // team_v2 权限与 ultimate_v2 对齐，仅通过配置声明，不在校验层额外加特殊逻辑
  duration: {
    options: {
      '16': ['standard_v2', 'premium_v2', 'ultimate_v2', 'team_v2'],
      '15': ['standard_v2', 'premium_v2', 'ultimate_v2', 'team_v2'],
      '14': ['standard_v2', 'premium_v2', 'ultimate_v2', 'team_v2'],
      '13': ['standard_v2', 'premium_v2', 'ultimate_v2', 'team_v2'],
      '12': ['standard_v2', 'premium_v2', 'ultimate_v2', 'team_v2'],
      '11': ['standard_v2', 'premium_v2', 'ultimate_v2', 'team_v2'],
      '10': ['standard_v2', 'premium_v2', 'ultimate_v2', 'team_v2'],
      '9': ['standard_v2', 'premium_v2', 'ultimate_v2', 'team_v2'],
      '8': ['standard_v2', 'premium_v2', 'ultimate_v2', 'team_v2'],
      '7': ['standard_v2', 'premium_v2', 'ultimate_v2', 'team_v2'],
      '6': ['standard_v2', 'premium_v2', 'ultimate_v2', 'team_v2'],
    },
    perVersionOverrides: {
      [MODEL_VERSION.Q3]: {
        '10': [], // 空数组表示所有用户都可以使用，包括免费用户
        '9': [], // 空数组表示所有用户都可以使用，包括免费用户
        '8': [], // 空数组表示所有用户都可以使用，包括免费用户
        '7': [], // 空数组表示所有用户都可以使用，包括免费用户
        '6': [], // 空数组表示所有用户都可以使用，包括免费用户
      },
      [MODEL_VERSION.Q3_A]: {
        '15': [],
        '14': [],
        '13': [],
        '12': [],
        '11': [],
        '10': [],
        '9': [],
        '8': [],
        '7': [],
        '6': [],
      },
      [MODEL_VERSION.Q3_HH]: {
        '15': [],
        '14': [],
        '13': [],
        '12': [],
        '11': [],
        '10': [],
        '9': [],
        '8': [],
        '7': [],
        '6': [],
      },
    },
  },
  model: {
    options: {
      'vidu-high-quality': ['standard_v2', 'premium_v2', 'ultimate_v2', 'team_v2'],
    },
  },
  resolution: {
    options: {
      '720p': ['standard_v2', 'premium_v2', 'ultimate_v2', 'team_v2'],
      '1080p': ['standard_v2', 'premium_v2', 'ultimate_v2', 'team_v2'],
    },
    perVersionOverrides: {
      [MODEL_VERSION.Q2]: {
        '1080p': [], // 空数组表示所有用户都可以使用，包括免费用户
      },
      [MODEL_VERSION.Q2_PRO]: {
        '1080p': [], // 空数组表示所有用户都可以使用，包括免费用户
      },
      [MODEL_VERSION.Q3]: {
        '1080p': [], // 空数组表示所有用户都可以使用，包括免费用户
      },
      [MODEL_VERSION.Q3_PRO]: {
        '1080p': [], // 空数组表示所有用户都可以使用，包括免费用户
      },
      [MODEL_VERSION.Q3_FAST]: {
        '1080p': [], // 空数组表示所有用户都可以使用，包括免费用户
      },
      [MODEL_VERSION.Q3_LITE]: {
        '1080p': [], // 空数组表示所有用户都可以使用，包括免费用户
      },
      [MODEL_VERSION.GPT_IMAGE_2]: {
        '1080p': [], // 空数组表示所有用户都可以使用，包括免费用户
      },
      [MODEL_VERSION.GPT_IMAGE_2_5_FAST]: {
        '1080p': [], // 空数组表示所有用户都可以使用，包括免费用户
      },
      [MODEL_VERSION.GPT_IMAGE_2_5_PRO]: {
        '1080p': [], // 空数组表示所有用户都可以使用，包括免费用户
      },
      [MODEL_VERSION.Q3_A]: {
        '720p': [], // 空数组表示所有用户都可以使用，包括免费用户
        '1080p': [], // 空数组表示所有用户都可以使用，包括免费用户
      },
      [MODEL_VERSION.Q3_HH]: {
        '720p': [], // 空数组表示所有用户都可以使用，包括免费用户
        '1080p': [], // 空数组表示所有用户都可以使用，包括免费用户
      },
    },
  },
  sample_count: {
    options: {
      '2': ['standard_v2', 'premium_v2', 'ultimate_v2', 'team_v2'],
      '3': ['standard_v2', 'premium_v2', 'ultimate_v2', 'team_v2'],
      '4': ['standard_v2', 'premium_v2', 'ultimate_v2', 'team_v2'],
    },
  },
  schedule_mode: {
    options: {
      nopeak: ['ultimate_v2', 'team_v2'],
    },
  },
  transition: {
    options: {
      pro: ['standard_v2', 'premium_v2', 'ultimate_v2', 'team_v2'],
    },
    perVersionOverrides: {
      [MODEL_VERSION.Q3]: {
        pro: [], // 空数组表示所有用户都可以使用，包括免费用户
        pro_ad: [],
      },
    },
  },
  codec: {
    options: {
      h265_pro: ['standard_v2', 'premium_v2', 'ultimate_v2', 'team_v2'],
      h264_pro: ['standard_v2', 'premium_v2', 'ultimate_v2', 'team_v2'],
    },
  },
};

const MODEL_IMAGE_MAP_GLOBAL: Record<ModelVersion, string> = {
  [MODEL_VERSION.Q3]: 'https://image01.cf.vidu.studio/vidu/media-asset/q3-712dcfbb.webp',
  [MODEL_VERSION.Q3_PRO]: 'https://image01.cf.vidu.studio/vidu/media-asset/banana-69e57a16.webp',
  [MODEL_VERSION.Q3_FAST]: 'https://image01.cf.vidu.studio/vidu/media-asset/banana-69e57a16.webp',
  [MODEL_VERSION.Q3_LITE]: 'https://image01.cf.vidu.studio/vidu/media-asset/banana-69e57a16.webp',
  [MODEL_VERSION.GPT_IMAGE_2]: 'https://image01-aliyun.vidu.zone/vidu/media-asset/gpt-image2-456ecc40.webp',
  [MODEL_VERSION.GPT_IMAGE_2_5_FAST]: 'https://image01-aliyun.vidu.zone/vidu/media-asset/gpt-image2-456ecc40.webp',
  [MODEL_VERSION.GPT_IMAGE_2_5_PRO]: 'https://image01-aliyun.vidu.zone/vidu/media-asset/gpt-image2-456ecc40.webp',
  [MODEL_VERSION.Q2_PRO]: 'https://image01.cf.vidu.studio/vidu/media-asset/q2-39d78492.webp', // Q2-Pro 使用 Q2 的图片
  [MODEL_VERSION.Q2]: 'https://image01.cf.vidu.studio/vidu/media-asset/q2-39d78492.webp',
  [MODEL_VERSION.Q1]: 'https://image01.cf.vidu.studio/vidu/media-asset/Q1-a1a97151.webp',
  [MODEL_VERSION['2.0']]: 'https://image01.cf.vidu.studio/vidu/media-asset/2-7d37f49c.webp',
  [MODEL_VERSION.Q3_A]: 'https://image01.cf.vidu.studio/vidu/media-asset/q3_a-b1cd300d.webp',
  [MODEL_VERSION.Q3_HH]: 'https://image01.cf.vidu.studio/vidu/media-asset/q3_a-b1cd300d.webp',
};
const MODEL_IMAGE_MAP_CHINA: Record<ModelVersion, string> = {
  [MODEL_VERSION.Q3]: 'https://image01-aliyun.vidu.zone/vidu/media-asset/q3-712dcfbb.webp',
  [MODEL_VERSION.Q3_PRO]: 'https://image01-aliyun.vidu.zone/vidu/media-asset/q3-712dcfbb.webp',
  [MODEL_VERSION.Q3_FAST]: 'https://image01-aliyun.vidu.zone/vidu/media-asset/q3-712dcfbb.webp',
  [MODEL_VERSION.Q3_LITE]: 'https://image01-aliyun.vidu.zone/vidu/media-asset/q3-712dcfbb.webp',
  [MODEL_VERSION.GPT_IMAGE_2]: 'https://image01.cf.vidu.studio/vidu/media-asset/gpt-image2-456ecc40.webp',
  [MODEL_VERSION.GPT_IMAGE_2_5_FAST]: 'https://image01.cf.vidu.studio/vidu/media-asset/gpt-image2-456ecc40.webp',
  [MODEL_VERSION.GPT_IMAGE_2_5_PRO]: 'https://image01.cf.vidu.studio/vidu/media-asset/gpt-image2-456ecc40.webp',
  [MODEL_VERSION.Q2_PRO]: 'https://image01-aliyun.vidu.zone/vidu/media-asset/q2-39d78492.webp', // Q2-Pro 使用 Q2 的图片
  [MODEL_VERSION.Q2]: 'https://image01-aliyun.vidu.zone/vidu/media-asset/q2-39d78492.webp',
  [MODEL_VERSION.Q1]: 'https://image01-aliyun.vidu.zone/vidu/media-asset/Q1-a1a97151.webp',
  [MODEL_VERSION['2.0']]: 'https://image01-aliyun.vidu.zone/vidu/media-asset/2-7d37f49c.webp',
  [MODEL_VERSION.Q3_A]: 'https://image01-aliyun.vidu.zone/vidu/media-asset/seedance-premium-48-e24c6312.webp',
  [MODEL_VERSION.Q3_HH]: 'https://image01-aliyun.vidu.zone/vidu/media-asset/happyhorse-real-horse-48-bb4a9108.webp',
};

export const MODEL_IMAGE_MAP = SITE === 'china' ? MODEL_IMAGE_MAP_CHINA : MODEL_IMAGE_MAP_GLOBAL;
